import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-light-svg-icons';
import {
  Card,
  DisabledButton,
  FontAwesomeButton,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import { useOrganization } from '@foxtrotplatform/platform-micro-site-core';
import { Button } from '@windmill/react-ui';
import React, { FunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  acceptTeamInvitation,
  declineTeamInvitation,
} from '../../common/api/team';
import { getPendingUserInviations } from '../../common/api/user';
import { DeveloperOrganizationContextProps } from '../../common/providers/types';
import {
  removeInvitation,
  setInvitations,
} from '../../common/reducers/invitationSlice';
import { addToast } from '../../common/reducers/toasterSlice';

export const UserInvitationList: FunctionComponent = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const { refreshOrganizations } =
    useOrganization() as DeveloperOrganizationContextProps;
  const invitations: Invitation[] = useSelector<AppState, Invitation[]>(
    ({ invitations }) => {
      return invitations;
    }
  );
  const [visibleInvitation, setVisibleInvitation] =
    React.useState<Invitation>();

  React.useEffect(() => {
    const getInvitations = async () => {
      const newInvitations = await getPendingUserInviations();
      if (newInvitations) {
        dispatch(setInvitations(newInvitations));
      }
    };

    getInvitations();
  }, []);

  React.useEffect(() => {
    if (invitations && invitations.length > 0 && !visibleInvitation) {
      setVisibleInvitation(invitations[0]);
    }
  }, [invitations]);

  const getVisibleInvitationIndex = React.useCallback(
    (visibleInvitation: Invitation) => {
      return invitations.findIndex(
        (invitation: Invitation) => invitation.id === visibleInvitation.id
      );
    },
    [invitations]
  );

  const acceptInvitation = async (invitation: Invitation) => {
    try {
      await acceptTeamInvitation(
        invitation.orgId,
        invitation.teamId,
        invitation.requestedEmail
      );
      refreshOrganizations();

      const filteredInvitations = invitations?.filter(
        (invite) => invite.id !== invitation.id
      );
      dispatch(removeInvitation(invitation.id));
      if (filteredInvitations && filteredInvitations?.length > 2) {
        setVisibleInvitation(filteredInvitations[0]);
      }
      const newToastState = {
        message: `Successfully added to the "${invitation.teamName}"`,
        isError: false,
      } as ToasterState;
      dispatch(addToast(newToastState));
    } catch {
      const newToastState = {
        message: `There was an error accepting invitation to "${invitation.teamName}"`,
        isError: true,
      } as ToasterState;
      dispatch(addToast(newToastState));
    }
  };

  const denyInvitation = async (invitation: Invitation) => {
    try {
      await declineTeamInvitation(
        invitation.orgId,
        invitation.teamId,
        invitation.requestedEmail,
        invitation.id
      );

      const filteredInvitations = invitations?.filter(
        (invite) => invite.id !== invitation.id
      );
      dispatch(removeInvitation(invitation.id));
      if (filteredInvitations && filteredInvitations?.length > 2) {
        setVisibleInvitation(filteredInvitations[0]);
      }
      const newToastState = {
        message: `Delined invitation to ${invitation.teamName}`,
        isError: false,
      } as ToasterState;
      dispatch(addToast(newToastState));
    } catch {
      const newToastState = {
        message: `There was an error declining your invitation to ${invitation.teamName}`,
        isError: true,
      } as ToasterState;
      dispatch(addToast(newToastState));
    }
  };

  if (!invitations || invitations.length <= 0 || !visibleInvitation) {
    return null;
  }

  return (
    <div className="flex flex-col p-os1-spacing">
      <Card className="flex rounded-lg bg-gray-50 p-os1-spacing w-103 h-32 relative">
        <div className="w-full flex flex-col">
          <div className="w-full flex justify-start pb-2.5">
            <span className="text-md">
              You've been invited to {visibleInvitation.teamName}
            </span>
          </div>
          <div className="w-full flex justify-end">
            <div className="flex flex-row gap-x-4">
              <Button
                className="py-2 px-4 border border-gray-200 text-gray-500 rounded-md bg-transparent"
                layout="outline"
                onClick={() => denyInvitation(visibleInvitation)}
              >
                Decline
              </Button>
              <DisabledButton
                className="py-2 px-4 rounded-md"
                onClick={() => acceptInvitation(visibleInvitation)}
                type="secondary"
              >
                Accept
              </DisabledButton>
            </div>
          </div>
        </div>
        <div className="rounded-full w-36 h-8 pl-4 pt-0.5 absolute left-2 top-neg-4 bg-white border border-gary-100 ">
          <span>
            Invitations ({getVisibleInvitationIndex(visibleInvitation) + 1}/
            {invitations.length}){' '}
          </span>
        </div>
        <div className="flex flex-row gap-x-4 absolute top-neg-4 right-2">
          <div className="rounded-full w-10 h-8 pl-3.5 pt-0.5  bg-white border border-gary-100">
            <FontAwesomeButton
              disabled={
                invitations.length <= 1 ||
                getVisibleInvitationIndex(visibleInvitation) === 0
              }
              icon={faChevronLeft}
              onClick={() =>
                setVisibleInvitation(
                  invitations[getVisibleInvitationIndex(visibleInvitation) - 1]
                )
              }
            />
          </div>
          <div className="rounded-full w-10 h-8 pl-3.5 pt-0.5 bg-white border border-gary-100">
            <FontAwesomeButton
              disabled={
                invitations.length <= 1 ||
                getVisibleInvitationIndex(visibleInvitation) ===
                  invitations.length - 1
              }
              icon={faChevronRight}
              onClick={() =>
                setVisibleInvitation(
                  invitations[getVisibleInvitationIndex(visibleInvitation) + 1]
                )
              }
            />
          </div>
        </div>
      </Card>
    </div>
  );
};
